import type { FormDataConvertible } from "@inertiajs/core"
import { router, usePage } from "@inertiajs/react"
import {
  Check,
  ChevronRight,
  ClipboardList,
  Copy,
  Globe,
  Loader2,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { formatPhone } from "@/components/agents/edit/receptionist/form-specs"
import { VoicePicker } from "@/components/agents/setup/receptionist/voice-picker"
import type { VoiceOption } from "@/components/agents/setup/types"
import {
  BrandColorInput,
  BrandColorSwatch,
} from "@/components/business-profile/brand-color-field"
import {
  DEFAULT_HOURS,
  type DayHours,
  HoursEditor,
  type HoursStatus,
  hoursSummary,
} from "@/components/business-profile/hours-editor"
import { LandingPageCtaClicks } from "@/components/landing-page-cta-clicks"
import { type BusinessProfilePayload } from "@/components/onboarding/types"
import {
  PlacesPicker,
  type PlacesPickerSelection,
} from "@/components/places-picker"
import AttributeDisplay from "@/components/ui/attribute-display"
import { Button } from "@/components/ui/button"
import { Empty } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import {
  businessDetailsPartnerAccountsPath,
  businessSearchPartnerAccountsPath,
  generateLandingPagePartnerAccountPath,
  updateBusinessProfilePartnerAccountPath,
  updateLandingPagePartnerAccountPath,
  updateProvisionalAgentPartnerAccountPath,
} from "@/routes"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LandingPageData {
  url: string
  heading_override: string | null
  subheading_override: string | null
  cta_enabled: boolean
}

export interface ProvisionalAgentData {
  voice: string
  custom_prompt_instructions: string | null
}

// IMP-508: all-time engagement on the account's landing page.
export interface LandingPageStats {
  visits: number
  unique_visitors: number
  test_calls: number
  cta_clicks: number
}

export interface LandingPageSession {
  id: number
  created_at: string
  location: string | null
  ip_address: string | null
  cta_counts: Record<string, number>
}

// IMP-508/531: the demo record that born the account — absent for
// hand-made accounts.
export interface AssessmentRecord {
  email: string | null
  created_at: string
  // IMP-516: the interview transcript (IMP-515) — empty for assessments
  // that predate it.
  conversation: { role: "assistant" | "user"; content: string }[]
}

interface LandingPageSectionProps {
  landingPage: LandingPageData | null
  generating: boolean
  businessProfile: BusinessProfilePayload | null
  voices: VoiceOption[]
  provisionalAgent: ProvisionalAgentData | null
}

export interface MarketingTabProps extends LandingPageSectionProps {
  stats: LandingPageStats | null
  sessions: LandingPageSession[]
  assessment: AssessmentRecord | null
}

// Inertia visits that keep the Resources tab active + the edit sheet open
// (no remount), refreshing only the props the action touched.
const KEEP_TAB = { preserveScroll: true, preserveState: true } as const

// The partner-scoped places-search + place-details endpoints backing every
// PlacesPicker here.
const SEARCH_PATH = businessSearchPartnerAccountsPath()
const DETAILS_PATH = businessDetailsPartnerAccountsPath()

function useAccountUid() {
  return (usePage().props as unknown as { account: { uid: string } }).account
    .uid
}

function generateLandingPage(
  accountUid: string,
  sel: PlacesPickerSelection,
  onFinish?: () => void,
) {
  router.post(
    generateLandingPagePartnerAccountPath(accountUid),
    {
      place_id: sel.place_id,
      name: sel.name,
      website: sel.website ?? "",
      address: sel.address ?? "",
    },
    { ...KEEP_TAB, onFinish },
  )
}

// ---------------------------------------------------------------------------
// MarketingTab (IMP-508, né ResourcesTab)
// ---------------------------------------------------------------------------

// Two flat sections side by side, split by a vertical rule (the mockup's
// 1fr / rule / 360px grid). Left: the landing page (idle / generating /
// generated as before) and — once generated — its engagement: four stat
// tiles over the account's session history. Right: the assessment record,
// or an empty state for accounts that didn't come through an assessment.
export function MarketingTab({
  stats,
  sessions,
  assessment,
  ...landingPageSection
}: MarketingTabProps) {
  // Mirror LandingPageSection's generated-state condition — engagement only
  // makes sense under a fully generated landing page.
  const generated =
    landingPageSection.landingPage &&
    landingPageSection.provisionalAgent &&
    landingPageSection.businessProfile &&
    !landingPageSection.generating
  const hasEngagement =
    stats &&
    (stats.visits > 0 ||
      stats.unique_visitors > 0 ||
      stats.test_calls > 0 ||
      stats.cta_clicks > 0)

  return (
    <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-[1fr_auto_360px]">
      <div className="min-w-0 space-y-4">
        <LandingPageSection {...landingPageSection} />
        {generated && stats && (
          <>
            {hasEngagement && <StatTiles stats={stats} />}
            <SessionsTable sessions={sessions} />
          </>
        )}
      </div>
      <Separator
        orientation="vertical"
        className="hidden self-stretch xl:block"
      />
      <AssessmentRecordSection assessment={assessment} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// StatTiles — the landing page's engagement at a glance. Compact tiles,
// smaller than the dashboard's StatCard so they don't compete with the
// section they're in.
// ---------------------------------------------------------------------------

function StatTiles({ stats }: { stats: LandingPageStats }) {
  const tiles = [
    { label: "Visits", value: stats.visits },
    { label: "Unique visitors", value: stats.unique_visitors },
    { label: "Test calls", value: stats.test_calls },
    { label: "CTA clicks", value: stats.cta_clicks },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-md border px-3 py-2.5">
          <div className="text-muted-foreground text-xs font-medium">
            {tile.label}
          </div>
          <div className="mt-1 text-xl font-medium tracking-tight">
            {tile.value}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SessionsTable — this account's landing-page sessions (the partner-wide
// table minus the account column)
// ---------------------------------------------------------------------------

function formatSessionTime(iso: string) {
  const d = new Date(iso)
  return `${d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })}, ${d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`
}

function SessionsTable({ sessions }: { sessions: LandingPageSession[] }) {
  if (sessions.length === 0) {
    return (
      <Empty
        icon={<Globe className="h-5 w-5" />}
        title="No sessions yet"
        description="Visits to this account's landing page will show up here."
      />
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Session</TableHead>
          <TableHead>Visitor</TableHead>
          <TableHead>CTA clicks</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.map((session) => (
          <TableRow key={session.id}>
            <TableCell className="text-muted-foreground text-sm">
              {formatSessionTime(session.created_at)}
            </TableCell>
            <TableCell className="text-sm">
              <div>
                {session.ip_address ?? (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div className="text-muted-foreground">
                {session.location ?? "—"}
              </div>
            </TableCell>
            <TableCell className="text-sm">
              <LandingPageCtaClicks ctaCounts={session.cta_counts} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// ---------------------------------------------------------------------------
// AssessmentRecordSection — the demo this business ran with the assistant
// ---------------------------------------------------------------------------

function AssessmentRecordSection({
  assessment,
}: {
  assessment: AssessmentRecord | null
}) {
  if (!assessment) {
    return (
      <div className="space-y-4">
        <SectionHeader
          title="Assessment"
          description="What this business shared during their assessment."
        />
        <Empty
          icon={<ClipboardList className="h-5 w-5" />}
          title="No assessment"
          description="This account didn't come through an assessment."
        />
      </div>
    )
  }

  const created = new Date(assessment.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Demo"
        description="What happened when this business met the assistant."
      />

      <div className="grid grid-cols-2 gap-6">
        <AttributeDisplay
          label="Contact"
          value={assessment.email ?? "—"}
          className="col-span-2"
        />
        <AttributeDisplay
          label="Created"
          value={created}
          className="col-span-2"
        />
      </div>

      <ConversationTranscript conversation={assessment.conversation ?? []} />
    </div>
  )
}

// IMP-516: what the prospect actually said, in their own words — collapsed
// behind an affordance so the record stays scannable. Read-only funnel data;
// absent entirely for assessments that predate the transcript (IMP-515).
function ConversationTranscript({
  conversation,
}: {
  conversation: { role: "assistant" | "user"; content: string }[]
}) {
  const [open, setOpen] = useState(false)
  if (conversation.length === 0) return null

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
      >
        <ChevronRight
          className={cn("size-3.5 transition-transform", open && "rotate-90")}
        />
        {open ? "Hide conversation" : "View conversation"}
      </button>
      {open && (
        <div className="flex max-h-96 flex-col gap-2 overflow-y-auto rounded-md border p-3">
          {conversation.map((entry, index) => (
            <div
              key={index}
              className={cn(
                "max-w-[90%] text-sm leading-snug",
                entry.role === "assistant"
                  ? "text-muted-foreground self-start"
                  : "bg-muted self-end rounded-lg px-2.5 py-1.5",
              )}
            >
              {entry.content}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// LandingPageSection — three states: idle / generating / generated
// ---------------------------------------------------------------------------

function LandingPageSection({
  landingPage,
  generating,
  businessProfile,
  voices,
  provisionalAgent,
}: LandingPageSectionProps) {
  const accountUid = useAccountUid()
  const [selection, setSelection] = useState<PlacesPickerSelection | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // While the scrape + provision job runs, poll until the URL + demo agent land.
  useEffect(() => {
    if (!generating) return
    const timer = setInterval(() => {
      router.reload({
        ...KEEP_TAB,
        only: [
          "landing_page",
          "landing_page_generating",
          "business_profile",
          "provisional_agent",
        ],
      })
    }, 2500)
    return () => clearInterval(timer)
  }, [generating])

  function handleGenerate() {
    if (!selection) return
    setSubmitting(true)
    generateLandingPage(accountUid, selection, () => setSubmitting(false))
  }

  // Generating state
  if (generating) {
    return (
      <div className="space-y-4">
        <LandingPageHeader />
        <GeneratingSkeleton />
      </div>
    )
  }

  // Generated state
  if (landingPage && provisionalAgent && businessProfile) {
    return (
      <GeneratedState
        landingPage={landingPage}
        businessProfile={businessProfile}
        voices={voices}
        provisionalAgent={provisionalAgent}
      />
    )
  }

  // Idle state — no landing page yet
  return (
    <div className="space-y-4">
      <LandingPageHeader />
      <div className="flex items-start gap-2">
        <PlacesPicker
          value={selection}
          onChange={setSelection}
          searchPath={SEARCH_PATH}
          detailsPath={DETAILS_PATH}
          placeholder="Search business name or paste website URL"
        />
        <Button
          type="button"
          disabled={!selection || submitting}
          loading={submitting}
          onClick={handleGenerate}
        >
          Generate
        </Button>
      </div>
    </div>
  )
}

function SectionHeader({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>
    </div>
  )
}

function LandingPageHeader() {
  return (
    <SectionHeader
      title="Landing page"
      description="Generate a shareable landing page with a live demo agent your client can try before activating."
    />
  )
}

// ---------------------------------------------------------------------------
// GeneratedState
// ---------------------------------------------------------------------------

function GeneratedState({
  landingPage,
  businessProfile,
  voices,
  provisionalAgent,
}: {
  landingPage: LandingPageData
  businessProfile: BusinessProfilePayload
  voices: VoiceOption[]
  provisionalAgent: ProvisionalAgentData
}) {
  const accountUid = useAccountUid()
  const [rescanning, setRescanning] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  function handleRescan(sel: PlacesPickerSelection | null) {
    setRescanning(false)
    if (sel) generateLandingPage(accountUid, sel)
  }

  return (
    <div className="space-y-4">
      <LandingPageHeader />

      <LandingPageCard
        url={landingPage.url}
        rescanning={rescanning}
        onRescan={() => setRescanning(true)}
        onCancelRescan={() => setRescanning(false)}
        onRescanSelect={handleRescan}
        onSettings={() => setEditOpen(true)}
      />

      <LandingPageEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        accountUid={accountUid}
        agent={provisionalAgent}
        landingPage={landingPage}
        businessProfile={businessProfile}
        voices={voices}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// LandingPageEditSheet
// ---------------------------------------------------------------------------

function LandingPageEditSheet({
  open,
  onOpenChange,
  ...body
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  accountUid: string
  agent: ProvisionalAgentData
  landingPage: LandingPageData
  businessProfile: BusinessProfilePayload
  voices: VoiceOption[]
}) {
  // The body owns the form state. Radix unmounts SheetContent's children when
  // the sheet closes, so the body remounts on each open — its useState
  // initializers re-seed from the latest props with no effect, and prop
  // refreshes mid-session (e.g. an immediate profile save) leave it untouched.
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-lg">
        <EditSheetBody {...body} />
      </SheetContent>
    </Sheet>
  )
}

function EditSheetBody({
  accountUid,
  agent,
  landingPage,
  businessProfile,
  voices,
}: {
  accountUid: string
  agent: ProvisionalAgentData
  landingPage: LandingPageData
  businessProfile: BusinessProfilePayload
  voices: VoiceOption[]
}) {
  const [activeTab, setActiveTab] = useState("profile")
  const [voice, setVoice] = useState(agent.voice)
  const [instructions, setInstructions] = useState(
    agent.custom_prompt_instructions ?? "",
  )
  const [heading, setHeading] = useState(landingPage.heading_override ?? "")
  const [subheading, setSubheading] = useState(
    landingPage.subheading_override ?? "",
  )
  const [saving, setSaving] = useState(false)

  function saveAgent() {
    setSaving(true)
    router.patch(
      updateProvisionalAgentPartnerAccountPath(accountUid),
      { voice, custom_prompt_instructions: instructions },
      { ...KEEP_TAB, onFinish: () => setSaving(false) },
    )
  }

  function savePage() {
    setSaving(true)
    router.patch(
      updateLandingPagePartnerAccountPath(accountUid),
      { heading_override: heading, subheading_override: subheading },
      { ...KEEP_TAB, onFinish: () => setSaving(false) },
    )
  }

  return (
    <>
      <SheetHeader className="px-6 py-4">
        <SheetTitle>Edit settings</SheetTitle>
      </SheetHeader>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <div className="border-b px-6">
          <TabsList variant="line">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="agent">Agent</TabsTrigger>
            <TabsTrigger value="landing_page">Page</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profile" className="flex-1 overflow-y-auto">
          <BusinessProfileCard
            accountUid={accountUid}
            profile={businessProfile}
          />
        </TabsContent>

        <TabsContent
          value="agent"
          className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
        >
          <section className="space-y-2">
            <div className="text-sm font-medium">Voice</div>
            <VoicePicker
              voices={voices}
              selected={voice}
              columns={3}
              onSelect={setVoice}
            />
          </section>

          <section className="space-y-2">
            <div>
              <div className="text-sm font-medium">Custom instructions</div>
              <div className="text-muted-foreground text-sm">
                Add any additional rules the agent should follow.
              </div>
            </div>
            <Textarea
              id="sheet-instructions"
              rows={6}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="resize-none"
            />
          </section>
        </TabsContent>

        <TabsContent
          value="landing_page"
          className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
        >
          <section className="space-y-2">
            <div>
              <div className="text-sm font-medium">Custom headline</div>
              <div className="text-muted-foreground text-sm">
                Replaces the auto-generated headline in the hero section.
              </div>
            </div>
            <Input
              id="sheet-heading"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
            />
          </section>

          <section className="space-y-2">
            <div>
              <div className="text-sm font-medium">Custom subheading</div>
              <div className="text-muted-foreground text-sm">
                Replaces the auto-generated subheading in the hero section.
              </div>
            </div>
            <Textarea
              id="sheet-subheading"
              rows={3}
              value={subheading}
              onChange={(e) => setSubheading(e.target.value)}
              className="resize-none"
            />
          </section>
        </TabsContent>
      </Tabs>

      {activeTab !== "profile" && (
        <SheetFooter className="border-t px-6 py-4">
          <Button
            className="w-full rounded-sm"
            loading={saving}
            onClick={activeTab === "agent" ? saveAgent : savePage}
          >
            Save changes
          </Button>
        </SheetFooter>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// LandingPageCard
// ---------------------------------------------------------------------------

function LandingPageCard({
  url,
  rescanning,
  onRescan,
  onCancelRescan,
  onRescanSelect,
  onSettings,
}: {
  url: string
  rescanning: boolean
  onRescan: () => void
  onCancelRescan: () => void
  onRescanSelect: (sel: PlacesPickerSelection | null) => void
  onSettings: () => void
}) {
  const [copied, setCopied] = useState(false)

  function copy() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="flex items-center gap-4">
      {rescanning ? (
        <div className="min-w-0 flex-1">
          <PlacesPicker
            value={null}
            onChange={onRescanSelect}
            searchPath={SEARCH_PATH}
            detailsPath={DETAILS_PATH}
            placeholder="Search business name or paste website URL"
          />
        </div>
      ) : (
        <div className="bg-muted/50 flex min-w-0 flex-1 items-center gap-2 rounded-md border px-3 py-2">
          <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">
            {url.replace(/^https?:\/\//, "")}
          </span>
          <button
            type="button"
            onClick={copy}
            className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
            aria-label="Copy link"
          >
            {copied ? (
              <Check className="size-3.5 text-green-600" />
            ) : (
              <Copy className="size-3.5" />
            )}
          </button>
        </div>
      )}

      <div className="flex shrink-0 gap-2">
        {rescanning ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancelRescan}
          >
            Cancel
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={onRescan}>
            Rescan
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onSettings}>
          Settings
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// BusinessProfileCard — click-to-edit rows; each save persists immediately
// ---------------------------------------------------------------------------

type EditField =
  | "business_name"
  | "website"
  | "phone"
  | "address"
  | "hours"
  | "business_category"
  | "description"
  | "services"
  | "brand_color"

// Only the columns the controller permits (place_id is read-only here). Typed
// as an Inertia payload so the nested hours array satisfies router.patch.
function toProfileParams(
  p: BusinessProfilePayload,
): Record<string, FormDataConvertible> {
  return {
    business_name: p.business_name ?? "",
    website: p.website ?? "",
    phone: p.phone ?? "",
    address: p.address ?? "",
    business_category: p.business_category ?? "",
    description: p.description ?? "",
    services: p.services ?? "",
    brand_color: p.brand_color ?? "",
    hours_status: p.hours_status ?? "open_no_hours",
    hours: (p.hours ?? DEFAULT_HOURS).map(
      (h): Record<string, FormDataConvertible> => ({
        day: h.day,
        abbr: h.abbr,
        open: h.open,
        all_day: h.all_day ?? false,
        from: h.from,
        to: h.to,
      }),
    ),
  }
}

function BusinessProfileCard({
  accountUid,
  profile: initialProfile,
}: {
  accountUid: string
  profile: BusinessProfilePayload
}) {
  const [profile, setProfile] = useState<BusinessProfilePayload>({
    ...initialProfile,
    hours:
      initialProfile.hours && initialProfile.hours.length > 0
        ? initialProfile.hours
        : DEFAULT_HOURS,
  })

  // Business-profile edits persist immediately (AC). Scope the partial reload
  // to business_profile so it doesn't disturb the open sheet's other tabs.
  const persist = useCallback(
    (next: BusinessProfilePayload) => {
      router.patch(
        updateBusinessProfilePartnerAccountPath(accountUid),
        { business_profile: toProfileParams(next) },
        { ...KEEP_TAB, only: ["business_profile"] },
      )
    },
    [accountUid],
  )

  const setField = useCallback(
    <K extends keyof BusinessProfilePayload>(
      key: K,
      value: BusinessProfilePayload[K],
    ) => {
      setProfile((prev) => {
        const next = { ...prev, [key]: value }
        persist(next)
        return next
      })
    },
    [persist],
  )

  const [editingField, setEditingField] = useState<EditField | null>(null)
  const [draft, setDraft] = useState("")
  const [draftHours, setDraftHours] = useState<DayHours[]>(DEFAULT_HOURS)
  const [draftStatus, setDraftStatus] = useState<HoursStatus>("open_no_hours")

  function startEdit(field: EditField) {
    if (field === "hours") {
      setDraftHours((profile.hours ?? DEFAULT_HOURS).map((h) => ({ ...h })))
      setDraftStatus(profile.hours_status ?? "open_no_hours")
    } else {
      setDraft(profile[field]! ?? "")
    }
    setEditingField(field)
  }

  function saveField() {
    if (editingField === null) return
    if (editingField === "hours") {
      // Persist status + schedule together in one request.
      setProfile((prev) => {
        const next = { ...prev, hours: draftHours, hours_status: draftStatus }
        persist(next)
        return next
      })
    } else {
      setField(editingField, draft)
    }
    setEditingField(null)
  }

  function cancelEdit() {
    setEditingField(null)
  }

  function updateDraftHour(i: number, patch: Partial<DayHours>) {
    setDraftHours((prev) =>
      prev.map((h, idx) => (idx === i ? { ...h, ...patch } : h)),
    )
  }

  const readOnlyText = (value: string | null | undefined) =>
    value ? (
      <span className="text-sm">{value}</span>
    ) : (
      <span className="text-muted-foreground text-sm italic">Not set</span>
    )

  return (
    <div className="divide-y border-b">
      <EditableRow
        label="Name"
        field="business_name"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.business_name)}
        editContent={
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Acme Corp"
          />
        }
      />
      <EditableRow
        label="Website"
        field="website"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.website)}
        editContent={
          <Input
            autoFocus
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://example.com"
          />
        }
      />
      <EditableRow
        label="Phone"
        field="phone"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.phone)}
        editContent={
          <Input
            autoFocus
            type="tel"
            value={draft}
            onChange={(e) => setDraft(formatPhone(e.target.value))}
            placeholder="(555) 000-0000"
          />
        }
      />
      <EditableRow
        label="Address"
        field="address"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.address)}
        editContent={
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="123 Main St, New York, NY 10001"
          />
        }
      />
      <EditableRow
        label="Brand color"
        field="brand_color"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={
          profile.brand_color ? (
            <span className="flex items-center gap-2 text-sm">
              <BrandColorSwatch color={profile.brand_color} />
              <span>{profile.brand_color.toUpperCase()}</span>
            </span>
          ) : (
            readOnlyText(null)
          )
        }
        editContent={
          <BrandColorInput value={draft} onChange={(v) => setDraft(v)} />
        }
      />
      <EditableRow
        label="Hours"
        field="hours"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={
          <span className="text-sm">
            {hoursSummary(
              profile.hours_status ?? "open_no_hours",
              profile.hours ?? DEFAULT_HOURS,
            )}
          </span>
        }
        editContent={
          <HoursEditor
            status={draftStatus}
            hours={draftHours}
            onStatusChange={setDraftStatus}
            onHourChange={updateDraftHour}
          />
        }
      />
      <EditableRow
        label="Industry"
        field="business_category"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.business_category)}
        editContent={
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Plumbing, Dental practice, Law firm"
          />
        }
      />
      <EditableRow
        label="About"
        field="description"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.description)}
        editContent={
          <Textarea
            autoFocus
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Tell us what the business does…"
          />
        }
      />
      <EditableRow
        label="Services"
        field="services"
        editingField={editingField}
        onStartEdit={startEdit}
        onSave={saveField}
        onCancel={cancelEdit}
        display={readOnlyText(profile.services)}
        editContent={
          <Textarea
            autoFocus
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="List the services offered…"
          />
        }
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// EditableRow — reusable click-to-edit field row
// ---------------------------------------------------------------------------

function EditableRow({
  label,
  field,
  editingField,
  onStartEdit,
  onSave,
  onCancel,
  display,
  editContent,
}: {
  label: string
  field: EditField
  editingField: EditField | null
  onStartEdit: (f: EditField) => void
  onSave: () => void
  onCancel: () => void
  display: React.ReactNode
  editContent: React.ReactNode
}) {
  const isEditing = editingField === field
  return (
    <div
      className={cn(
        "group flex gap-3 px-4 py-3 transition-colors",
        isEditing ? "items-start" : "items-baseline",
        isEditing ? "bg-muted/40" : "hover:bg-muted/30 cursor-pointer",
      )}
      onClick={!isEditing ? () => onStartEdit(field) : undefined}
    >
      <span className="text-muted-foreground w-20 shrink-0 text-sm">
        {label}
      </span>
      {isEditing ? (
        <div className="flex-1 space-y-3" onClick={(e) => e.stopPropagation()}>
          {editContent}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" type="button" onClick={onSave}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1">{display}</div>
          <span className="text-muted-foreground ml-2 shrink-0 text-xs opacity-0 transition-opacity group-hover:opacity-100">
            Edit
          </span>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// GeneratingSkeleton
// ---------------------------------------------------------------------------

function GeneratingSkeleton() {
  return (
    <div className="rounded-lg border px-4 py-3">
      <div className="flex items-center gap-3">
        <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" />
        <span className="text-muted-foreground text-sm">
          Analyzing website and setting up demo agent…
        </span>
      </div>
    </div>
  )
}
